import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MessageCircle, Brain, CreditCard } from "lucide-react";

export function Support() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", message: "", topic: "support" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent");
    } catch { toast.error("Could not send"); }
  };

  return (
    <div data-testid="support-page" className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-3xl">
      <div className="dms-label mb-3">Help</div>
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342]">Support</h1>
      <p className="mt-2 text-[#475569]">Stuck on something? Send a note. We reply within 24 hours.</p>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { icon: Brain, t: "Dive Brain issues", d: "Outputs not on-brand" },
          { icon: CreditCard, t: "Billing", d: "Plan, invoice, refund" },
          { icon: MessageCircle, t: "Feature requests", d: "Tell us what you need" },
        ].map((c) => (
          <div key={c.t} className="dms-card p-5">
            <c.icon className="w-5 h-5 text-[#1D4E89]" />
            <div className="mt-3 font-heading font-bold text-[15px] text-[#0A2342]">{c.t}</div>
            <div className="mt-1 text-[13px] text-[#64748B]">{c.d}</div>
          </div>
        ))}
      </div>

      {sent ? (
        <div data-testid="support-sent" className="mt-8 dms-card p-6 bg-[#F8FAFC]">
          <div className="font-heading font-bold text-[#0A2342]">Message received.</div>
          <p className="mt-2 text-[#475569] text-[14.5px]">We&apos;ve sent a confirmation to {form.email}. Talk soon.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 dms-card p-6 space-y-4">
          <div>
            <label className="dms-label block mb-2">Name</label>
            <input data-testid="support-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="dms-input" />
          </div>
          <div>
            <label className="dms-label block mb-2">Email</label>
            <input data-testid="support-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="dms-input" />
          </div>
          <div>
            <label className="dms-label block mb-2">Message</label>
            <textarea data-testid="support-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="dms-textarea" />
          </div>
          <div className="flex justify-end">
            <button data-testid="support-submit" type="submit" className="dms-btn-primary">Send <ArrowRight className="w-4 h-4" /></button>
          </div>
        </form>
      )}
    </div>
  );
}

export function Account() {
  const { user } = useAuth();
  const { sub, badgeForTier } = useSubscription();
  const [brain, setBrain] = useState(null);

  useEffect(() => {
    api.get("/dive-brain").then(({ data }) => setBrain(data.brain)).catch(() => setBrain(null));
  }, []);

  return (
    <div data-testid="account-page" className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-3xl">
      <div className="dms-label mb-3">Profile</div>
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342]">Account</h1>

      <div className="mt-6 dms-card p-6 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-[#1D4E89] text-white flex items-center justify-center font-semibold">
          {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div data-testid="account-name" className="font-heading font-bold text-[#0A2342]">{user?.name || "-"}</div>
          <div data-testid="account-email" className="text-sm text-[#64748B] flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {user?.email}</div>
          <div className="mt-1.5 text-xs text-[#64748B]">Joined {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}</div>
        </div>
        <div className="hidden sm:inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.18em] uppercase font-bold px-2.5 py-1 rounded-full bg-[#0A2342] text-[#4FD1C5]">
          {badgeForTier ? badgeForTier(sub?.tier) : "Free trial"}
        </div>
      </div>

      <div className="mt-4 dms-card p-6" data-testid="account-brain-summary">
        <div className="flex items-center justify-between mb-3">
          <div className="dms-label">Your Dive Brain</div>
          <Link to="/dive-brain" className="text-[12.5px] font-semibold text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1">Open Dive Brain <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        {brain ? (
          <div className="space-y-3 text-[14px]">
            <div><span className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B]">Identity</span><div className="mt-0.5 text-[#1F2937]">{brain.identity || "-"}</div></div>
            <div><span className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B]">Niche</span><div className="mt-0.5 text-[#1F2937]">{brain.niche || "-"}</div></div>
            <div><span className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B]">Audience</span><div className="mt-0.5 text-[#1F2937] whitespace-pre-wrap">{brain.audience || "-"}</div></div>
            <div><span className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B]">Positioning</span><div className="mt-0.5 text-[#1F2937] whitespace-pre-wrap">{brain.positioning || "-"}</div></div>
          </div>
        ) : (
          <div className="text-[#94A3B8] text-sm">Complete onboarding to build your Dive Brain.</div>
        )}
      </div>
    </div>
  );
}

export default Support;
